import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Router} from '@angular/router';
import {BaseService, ConfigurationService, UUID} from '@nh/angular-tses';
import {environment} from 'environments/environment';
import {catchError, map} from 'rxjs/operators';
import {DepartureIdLookupEntry} from '../../departures/departure/departure-id-lookup.entry';

@Injectable({
  providedIn: 'root'
})
export class DepartureIdLookupService extends BaseService {
  private static readonly DEPARTURE_ENDPOINT: string = `${environment.tsesConfig.apiHost}/departure`;

  private static readonly ERROR_KEY: string = 'id-lookup-error';

  private readonly fragmentIdToLookupEntry: Record<string, Promise<DepartureIdLookupEntry>> = {};

  constructor(
    snackbar: MatSnackBar,
    configurationService: ConfigurationService,
    private http: HttpClient,
    private router: Router
  ) {
    super(snackbar, configurationService);
  }

  /**
   * Returns a promise resolving to the departure id lookup entry corresponding to the given
   * departure fragment id.
   *
   * @param fragmentId The departure fragment id
   */
  public getDepartureEntry(fragmentId: UUID | string): Promise<DepartureIdLookupEntry> {
    if (!fragmentId) {
      return Promise.reject('fragmentId was null');
    }

    const stringFragmentId: string = fragmentId instanceof UUID ? fragmentId.value : fragmentId;

    if (!this.fragmentIdToLookupEntry[stringFragmentId]) {
      const params: {[param: string]: string | string[]} = {fragmentId: stringFragmentId};

      this.fragmentIdToLookupEntry[stringFragmentId] = this.http
        .get<DepartureIdLookupEntry>(`${DepartureIdLookupService.DEPARTURE_ENDPOINT}/entry`, {params})
        .pipe(
          catchError((err: HttpErrorResponse) => {
            this._handleError(err, `Failed to get the departure from the database`, DepartureIdLookupService.ERROR_KEY);
            return Promise.reject(err);
          })
        )
        .toPromise();
    }

    return Promise.resolve(this.fragmentIdToLookupEntry[stringFragmentId]);
  }

  /**
   * Returns a promise resolving to an array of all the departure lookup entries that correspond
   * to the given numerical departure id. This will therefore return a lookup entry for every
   * revision of the departure.
   *
   * @param departureId The numerical departure id.
   */
  public getDepartureEntries(departureId: number): Promise<DepartureIdLookupEntry[]> {
    if (!departureId) {
      return Promise.reject('departureId was null');
    }

    return this.http
      .get<DepartureIdLookupEntry[]>(`${DepartureIdLookupService.DEPARTURE_ENDPOINT}/${departureId}/entries`)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this._handleError(
            err,
            `Failed to get the revisions for departure: ${departureId}`,
            DepartureIdLookupService.ERROR_KEY
          );
          return Promise.reject(err);
        })
      )
      .toPromise();
  }

  /**
   * Returns a promise resolving to the departure fragment id that corresponds to the latest
   * revision of the given departure.
   *
   * @param departureId The numerical departure id.
   */
  public getFragmentIdForLatestRevision(departureId: number): Promise<UUID> {
    if (!departureId) {
      return Promise.reject('departureId was null');
    }

    return this.http
      .get<string>(`${DepartureIdLookupService.DEPARTURE_ENDPOINT}/${departureId}/latest-revision`)
      .pipe(
        map((departureFragmentId: string) => {
          return UUID.orThrow(departureFragmentId);
        }),
        catchError((err: HttpErrorResponse) => {
          this._handleError(
            err,
            `Failed to get latest revision for departure: ${departureId}`,
            DepartureIdLookupService.ERROR_KEY
          );
          return Promise.reject(err);
        })
      )
      .toPromise();
  }

  public departureExists(departureId: number): Promise<boolean> {
    return this.http
      .get<string>(`${DepartureIdLookupService.DEPARTURE_ENDPOINT}/${departureId}/latest-revision`)
      .toPromise()
      .then(() => true, () => false);
  }

  /**
   * Returns a promise resolving to the latest revision number for the given departure.
   *
   * @param departureId The numerical departure id.
   */
  public getLatestRevision(departureId: number): Promise<number> {
    if (!departureId) {
      return Promise.reject('departureId was null');
    }

    return this.http
      .get<number>(`${DepartureIdLookupService.DEPARTURE_ENDPOINT}/${departureId}/latest-revision/number`)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this._handleError(
            err,
            `Failed to get latest revision for departure: ${departureId}`,
            DepartureIdLookupService.ERROR_KEY
          );
          return Promise.reject(err);
        })
      )
      .toPromise();
  }

  public navigateTo(fragmentId: UUID | string): Promise<boolean> {
    return this.getDepartureEntry(fragmentId).then((departureEntry: DepartureIdLookupEntry) => {
      const departureId: number = departureEntry.departureId;
      const revisionNumber: number = departureEntry.revision;
      return this.navigateToRevision(departureId, revisionNumber);
    });
  }

  public navigateToRevision(departureId: number, revisionNumber: number): Promise<boolean> {
    return this.router.navigate([`/departures/${departureId}/revisions/${revisionNumber}`]);
  }

  public addLookupEntry(fragmentId: UUID | string, departureEntry: DepartureIdLookupEntry): void {
    this.fragmentIdToLookupEntry[fragmentId instanceof UUID ? fragmentId.value : fragmentId] = Promise.resolve(
      departureEntry
    );
  }
}
